import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { useClassNames, Button } from '@vodafoneis/sjonvarpskjarni-js-lib';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeOff, faVolumeUp } from '@fortawesome/free-solid-svg-icons';
import EventUtility from '../../../../utils/EventUtility';
import { PLAYER_EVENTS } from '../../../../models/THEOPlayer';
import { PlaybackInformationContext } from '../../../../contexts/PlaybackInformationContext';

export const PlaybackSettingsVolume: React.FC = () => {
	const { player, playbackInformationVisible } = useContext(PlaybackInformationContext);

	const volumeBar = useRef(null);
	const [open, setOpen] = useState(false);
	const [volume, setVolume] = useState(player.getVolume());
	const isDragging = useRef(false);

	useEffect(() => {
		const onVolumeChanged = ({ volume: playerVolume }) => {
			const newVolume = player.isMuted() ? 0 : playerVolume;

			setVolume(newVolume);
			setOpen(true);
		};

		player.addEventListener(PLAYER_EVENTS.VOLUME_CHANGE, onVolumeChanged);

		return () => {
			if (player) {
				player.removeEventListener(PLAYER_EVENTS.VOLUME_CHANGE, onVolumeChanged);
			}
		};
	}, [player, volume]);

	useEffect(() => {
		if (open && !playbackInformationVisible) {
			setOpen(false);
		}
	}, [open, playbackInformationVisible]);

	const onButtonClicked = useCallback(
		(event) => {
			event.stopPropagation();
			event.preventDefault();
			setOpen(!open);
		},
		[open]
	);

	const calculateVolumeFromEvent = useCallback((event) => {
		const offset = new EventUtility(event).getClientY();
		const element = volumeBar.current;

		if (element) {
			const offsetBottom = element.getBoundingClientRect().y + element.clientHeight;

			const newVolume = (offsetBottom - offset) / element.clientHeight;

			if (newVolume < 0.05) {
				return 0;
			}

			if (newVolume > 0.95) {
				return 1;
			}

			return newVolume;
		}
		return 0;
	}, []);

	const changeVolume = useCallback(
		(newVolume) => {
			setVolume(newVolume);
			if (player) {
				player.setMuted(false);
				player.setVolume(newVolume);
			}
		},
		[player]
	);

	const onDragStart = useCallback((event) => {
		event.stopPropagation();
		isDragging.current = true;
	}, []);

	const onDragEnd = useCallback((event) => {
		event.stopPropagation();
		isDragging.current = false;
	}, []);

	const onDrag = useCallback(
		(event) => {
			if (isDragging.current === true) {
				const newVolume = calculateVolumeFromEvent(event);
				changeVolume(newVolume);
			}
		},
		[calculateVolumeFromEvent, changeVolume]
	);

	const onClick = useCallback(
		(event) => {
			event.preventDefault();
			event.stopPropagation();
			const newVolume = calculateVolumeFromEvent(event);
			changeVolume(newVolume);
		},
		[calculateVolumeFromEvent, changeVolume]
	);

	const volumePercentage = useMemo(() => {
		return volume * 100;
	}, [volume]);

	const className = useClassNames('playback-settings-volume', { selected: open });

	return (
		<div className={className}>
			<div
				className={'playback-settings-volume-slider-container'}
				onMouseDown={onDragStart}
				onMouseLeave={onDragEnd}
				onMouseUp={onDragEnd}
				onMouseMove={onDrag}
				onTouchStart={onDragStart}
				onTouchEnd={onDragEnd}
				onTouchMove={onDrag}
				onClick={onClick}
			>
				<div ref={volumeBar} className={'playback-settings-volume-slider'}>
					<div className={'volume-remaining'} style={{ height: `${100 - volumePercentage}%` }} />
					<div className={'volume-indicator'} />
					<div className={'volume'} style={{ height: `${volumePercentage}%` }} />
				</div>
			</div>
			<Button className={'playback-settings-volume-button'} onClick={onButtonClicked}>
				<FontAwesomeIcon icon={volumePercentage > 5 ? faVolumeUp : faVolumeOff} />
			</Button>
		</div>
	);
};
